import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Message } from 'primereact/message'
import { useAtomValue } from 'jotai'
import type { Product_Output, ScheduledTask_Output, StaffMember } from '@/api'
import { toastAtom } from '@/atoms/toastAtom'
import type { ShiftAssignments, ShiftType } from '../hooks/useDetailedPlanFlow'
import { SHIFT_LABELS } from '../hooks/useDetailedPlanFlow'
import {
  buildStaffColorMap,
  buildStaffOptionsByShift,
  findStaffLocations,
  formatStaffLocations,
  getCategoryLabel,
  getStaffCapabilityWarnings,
  isStocker,
  LIMIT_HIGHLIGHT_MS,
  LIMIT_HIGHLIGHT_STYLE,
  type StaffOption,
} from './staffUtils'

const SHIFTS: ShiftType[] = ['day', 'evening', 'night']
const SLOT_COUNT = 2

export type SlotsByShift = Record<ShiftType, (string | null)[]>

export interface StaffAssignmentEditTarget {
  date: string
  dateLabel: string
  machineId: string
}

interface StaffAssignmentEditDialogProps {
  target: StaffAssignmentEditTarget | null
  staffList: StaffMember[]
  assignments: ShiftAssignments
  // 対象日・対象成形機に予定されているタスク（SKU 表示と担当可否チェックに使う）
  tasks: ScheduledTask_Output[]
  productBySku: Record<string, Product_Output>
  onApply: (date: string, machineId: string, slotsByShift: SlotsByShift) => void
  onHide: () => void
}

const emptySlots = (): (string | null)[] => Array.from({ length: SLOT_COUNT }, () => null)

const readSlots = (assignments: ShiftAssignments, date: string, shift: ShiftType, machineId: string): (string | null)[] => {
  const raw = assignments[date]?.[shift]?.[machineId]
  const out = emptySlots()
  if (Array.isArray(raw)) {
    for (let i = 0; i < SLOT_COUNT; i++) {
      out[i] = typeof raw[i] === 'string' ? raw[i] : null
    }
  }
  return out
}

/**
 * 最適化結果画面から、特定の日・成形機のスタッフ割当（昼・夕・夜 × 2枠）を修正するダイアログ。
 * 編集はローカルの下書きに対して行い、「適用」で親の assignments に反映する。
 */
const StaffAssignmentEditDialog: React.FC<StaffAssignmentEditDialogProps> = ({
  target,
  staffList,
  assignments,
  tasks,
  productBySku,
  onApply,
  onHide,
}) => {
  const toast = useAtomValue(toastAtom)
  const [draft, setDraft] = useState<SlotsByShift>({ day: emptySlots(), evening: emptySlots(), night: emptySlots() })
  // 「同日に2件まで」で弾いたスタッフ。ダイアログ内の該当枠を一定時間強調する
  const [limitHighlightName, setLimitHighlightName] = useState<string | null>(null)
  const limitHighlightTimerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (limitHighlightTimerRef.current != null) window.clearTimeout(limitHighlightTimerRef.current)
    },
    [],
  )

  // ダイアログを開いた時点の割当を下書きに読み込む
  useEffect(() => {
    setLimitHighlightName(null)
    if (!target) return
    setDraft({
      day: readSlots(assignments, target.date, 'day', target.machineId),
      evening: readSlots(assignments, target.date, 'evening', target.machineId),
      night: readSlots(assignments, target.date, 'night', target.machineId),
    })
    // 開いた瞬間のスナップショットで良いので assignments の変化には追従しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  const staffOptionsByShift = useMemo(() => buildStaffOptionsByShift(staffList), [staffList])
  const staffColorByName = useMemo(() => buildStaffColorMap(staffList), [staffList])
  const staffByName = useMemo(() => {
    const map: Record<string, StaffMember> = {}
    for (const s of staffList) map[s.name] = s
    return map
  }, [staffList])

  const getStaffColorClass = useCallback(
    (name: string | null | undefined) => {
      if (!name) return 'bg-gray-200 text-700'
      return staffColorByName[name] ?? 'bg-surface-200 text-900'
    },
    [staffColorByName],
  )

  const renderStaffOption = useCallback(
    (option: StaffOption) => (
      <div className={`px-2 py-0 border-round-sm ${getStaffColorClass(option.value)}`}>{option.label}</div>
    ),
    [getStaffColorClass],
  )

  const renderSelectedStaff = useCallback(
    (option: StaffOption | null) => {
      if (!option) return <span className="text-500">未割り当て</span>
      return (
        <div className={`px-2 py-0 border-round-sm inline-block ${getStaffColorClass(option.value)}`}>{option.label}</div>
      )
    },
    [getStaffColorClass],
  )

  // 同日の他成形機（オペレーター含む）での割当件数。下書き中の自成形機分は別途加算する。
  const otherMachineCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    if (!target) return counts
    for (const shift of SHIFTS) {
      const byMachine = assignments[target.date]?.[shift] ?? {}
      for (const [machineId, slots] of Object.entries(byMachine)) {
        if (machineId === target.machineId) continue
        for (const name of slots ?? []) {
          if (!name) continue
          counts[name] = (counts[name] ?? 0) + 1
        }
      }
    }
    return counts
  }, [assignments, target])

  const draftCounts = useMemo(() => {
    const counts: Record<string, number> = { ...otherMachineCounts }
    for (const shift of SHIFTS) {
      for (const name of draft[shift]) {
        if (!name) continue
        counts[name] = (counts[name] ?? 0) + 1
      }
    }
    return counts
  }, [draft, otherMachineCounts])

  const skuIds = useMemo(() => tasks.map((t) => t.sku_id), [tasks])

  const requiresTwoInspectors = useMemo(
    () =>
      target != null &&
      tasks.some((t) => productBySku[t.sku_id]?.machine_variants?.[target.machineId]?.requires_two_inspectors === true),
    [tasks, productBySku, target],
  )

  const handleSlotChange = useCallback(
    (shift: ShiftType, slotIndex: number, value: string | null) => {
      const resolved = typeof value === 'string' ? value : null
      const current = draft[shift]
      const existsInSameMachine =
        !!resolved && current.some((name, idx) => idx !== slotIndex && name === resolved)
      if (existsInSameMachine) {
        toast?.show({
          severity: 'warn',
          summary: '重複割当不可',
          detail: `${resolved} さんは同じ日・同じ機械に重複して割り当てできません`,
          life: 3000,
        })
        return
      }
      const currentValue = current[slotIndex]
      const count = resolved ? (draftCounts[resolved] ?? 0) : 0
      if (resolved && !isStocker(resolved) && resolved !== currentValue && count >= 2) {
        // 他成形機は確定済みの割当、この成形機は下書きで割当先を求める
        const dayAssignments = target ? assignments[target.date] : undefined
        const merged = Object.fromEntries(
          SHIFTS.map((s) => [s, { ...dayAssignments?.[s], ...(target ? { [target.machineId]: draft[s] } : {}) }]),
        ) as Record<ShiftType, Record<string, (string | null)[]>>
        const locations = formatStaffLocations(findStaffLocations(merged, resolved), SHIFT_LABELS)
        toast?.show({
          severity: 'warn',
          summary: '割当上限',
          detail: `${resolved} さんは同日に2件までです（割当済み: ${locations}）`,
          life: LIMIT_HIGHLIGHT_MS,
        })
        setLimitHighlightName(resolved)
        if (limitHighlightTimerRef.current != null) window.clearTimeout(limitHighlightTimerRef.current)
        limitHighlightTimerRef.current = window.setTimeout(() => setLimitHighlightName(null), LIMIT_HIGHLIGHT_MS)
        return
      }
      setDraft((prev) => {
        const updated = [...prev[shift]]
        updated[slotIndex] = resolved
        return { ...prev, [shift]: updated }
      })
    },
    [draft, draftCounts, toast, target, assignments],
  )

  const isChanged = useMemo(() => {
    if (!target) return false
    for (const shift of SHIFTS) {
      const original = readSlots(assignments, target.date, shift, target.machineId)
      for (let i = 0; i < SLOT_COUNT; i++) {
        if ((original[i] ?? null) !== (draft[shift][i] ?? null)) return true
      }
    }
    return false
  }, [assignments, draft, target])

  const handleApply = useCallback(() => {
    if (!target) return
    onApply(target.date, target.machineId, {
      day: [...draft.day],
      evening: [...draft.evening],
      night: [...draft.night],
    })
    onHide()
  }, [target, draft, onApply, onHide])

  const activeShiftCount = SHIFTS.filter((shift) => draft[shift].some((n) => !!n)).length

  return (
    <Dialog
      header={target ? `${target.dateLabel} ${target.machineId} スタッフ割当の修正` : ''}
      visible={!!target}
      style={{ width: '560px' }}
      onHide={onHide}
      modal
      draggable={false}
      footer={
        <div className="flex justify-content-between align-items-center">
          <span className="text-xs text-600">稼働シフト: {activeShiftCount} / 3（{activeShiftCount * 8}h）</span>
          <div className="flex gap-2">
            <Button label="キャンセル" severity="secondary" size="small" outlined onClick={onHide} />
            <Button label="適用" icon="pi pi-check" size="small" onClick={handleApply} disabled={!isChanged} />
          </div>
        </div>
      }
    >
      {target && (
        <div className="flex flex-column gap-3">
          {/* 対象日の生産品 */}
          <div>
            <div className="text-sm font-semibold text-700 mb-1">この日の生産品</div>
            {tasks.length === 0 ? (
              <span className="text-sm text-500">生産予定なし</span>
            ) : (
              <div className="flex flex-column gap-0">
                {tasks.map((t) => {
                  const product = productBySku[t.sku_id]
                  const variant = product?.machine_variants?.[target.machineId]
                  const catLabel = getCategoryLabel(product)
                  return (
                    <span key={t.task_id} className="text-xs text-600">
                      {t.sku_id} × {t.quantity.toLocaleString('en-US')}
                      <span className={`ml-1 font-semibold ${catLabel.className}`}>{catLabel.label}</span>
                      {variant?.is_dual_inspection_compatible === true && (
                        <span className="ml-1 text-red-500 font-semibold">二台検査可</span>
                      )}
                      {variant?.requires_two_inspectors === true && (
                        <span className="ml-1 text-orange-600 font-semibold">検査員2名</span>
                      )}
                    </span>
                  )
                })}
              </div>
            )}
          </div>

          {/* シフト × 枠 */}
          <div className="flex flex-column gap-2">
            {SHIFTS.map((shift) => {
              const slots = draft[shift]
              const warnings = getStaffCapabilityWarnings(slots, skuIds, staffByName, productBySku)
              const inspectorCount = slots.filter((s) => !!s && !isStocker(s)).length
              const hasDoubleInspection = slots.some(
                (name) => !!name && !isStocker(name) && (draftCounts[name] ?? 0) >= 2,
              )
              const isFullyUnassigned = slots.every((s) => !s)
              return (
                <div
                  key={shift}
                  className={`flex align-items-start gap-3 p-2 border-round-sm border-1 surface-border ${isFullyUnassigned ? 'bg-gray-100' : ''}`}
                >
                  <div className="font-semibold text-sm pt-2" style={{ width: '2.5rem', flexShrink: 0 }}>
                    {SHIFT_LABELS[shift]}
                  </div>
                  <div className="flex flex-column gap-1 flex-1">
                    {Array.from({ length: SLOT_COUNT }, (_, slotIndex) => {
                      const highlighted = !!limitHighlightName && slots[slotIndex] === limitHighlightName
                      return (
                        <Dropdown
                          key={slotIndex}
                          value={slots[slotIndex]}
                          options={staffOptionsByShift[shift]}
                          onChange={(e) => handleSlotChange(shift, slotIndex, e.value)}
                          placeholder="未割り当て"
                          className={`w-full p-inputtext-sm ${highlighted ? 'bg-red-100' : ''}`}
                          style={highlighted ? LIMIT_HIGHLIGHT_STYLE : undefined}
                          showClear
                          itemTemplate={renderStaffOption}
                          valueTemplate={renderSelectedStaff}
                        />
                      )
                    })}
                    {warnings.map((w, wi) => (
                      <span key={wi} className="text-xs text-orange-600">⚠ {w}</span>
                    ))}
                    {hasDoubleInspection && (
                      <span className="text-xs text-red-500 font-semibold">二台検査</span>
                    )}
                    {requiresTwoInspectors && !isFullyUnassigned && inspectorCount < 2 && (
                      <span className="text-xs text-orange-600 font-semibold">⚠ 検査員2名必要</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <Message
            severity="info"
            className="w-full"
            text="適用すると帳票（印刷）には即時反映されます。稼働時間が変わると最適化結果が変わる可能性があるため、必要に応じて「再最適化」を実行してください。"
          />
        </div>
      )}
    </Dialog>
  )
}

export default StaffAssignmentEditDialog
